import { useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import CollectorLegendControl from '@App/components/Map/controls/CollectorLegendControl/CollectorLegendControl';
import PanelHeader from '@App/components/PanelHeader/PanelHeader';
import PanelList, {
  getPropertyRecord,
} from '@App/components/PanelList/PanelList';
import { useSewageWaterDetails } from '@services/query.service';
import { getFeaturesBounds } from '@utils/geometry.utils';

import {
  getSourceColorExpression,
  layers,
  sources,
} from '../sources.layers';

import { inactiveSourceLayers } from './sewage-water.layers';

import './sewage-water.css';

const activeRwziLayerId = 'active-rwzi';
const activeRwziSourceId = 'active-rwzi-source';

const numberFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});

function formatEnergyPerYear(value) {
  if (value === undefined || value === null || value === '') return '-';

  const numericValue = Number(value);
  const absoluteValue = Math.abs(numericValue);
  if (absoluteValue >= 1_000_000) {
    return `${numberFormatter.format(numericValue / 1_000_000)} GWh/jaar`;
  }
  if (absoluteValue >= 1_000) {
    return `${numberFormatter.format(numericValue / 1_000)} MWh/jaar`;
  }

  return `${numberFormatter.format(numericValue)} kWh/jaar`;
}

function SewageWater() {
  const { map } = useOutletContext();
  const { rwziId } = useParams();
  const navigate = useNavigate();
  const { data } = useSewageWaterDetails(rwziId);
  const record = getPropertyRecord(data);
  const name = record.Gemeente ? `RWZI ${record.Gemeente}` : 'RWZI';
  const details = [
    ['Beschikbaarheidsstatus', 'Nog niet beschikbaar'],
    [
      'Piek bronpotentieel',
      formatEnergyPerYear(record.maximaal_piek_bronpotentieel),
    ],
    [
      'Warmte geleverd',
      formatEnergyPerYear(record.totale_warmte_geleverd_combosleutel_bs6_vs1),
    ],
  ];

  useEffect(() => {
    if (!map) return undefined;

    const legendControl = new CollectorLegendControl({
      'potential': 900,
      'heat-delivered': 200,
    });
    map.addControl(legendControl, 'bottom-right');

    return () => {
      map.removeControl(legendControl);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !data?.geometry) return undefined;

    const featureBounds = getFeaturesBounds([data]);
    if (!featureBounds) return undefined;

    const frame = requestAnimationFrame(() => {
      map.resize();
      map.fitBounds(featureBounds, {
        padding: {
          top: 48,
          right: 48,
          bottom: 48,
          left: 48,
        },
        maxZoom: 16,
        duration: 1000,
      });
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [data, map]);

  useEffect(() => {
    if (!map || !data?.geometry) return undefined;

    const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;
    if (!s3BucketUrl) return undefined;

    const sourceEntries = Object.values(sources);
    const layerEntries = Object.values(layers);
    sourceEntries.forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, {
          type: source.type,
          url: `pmtiles://${s3BucketUrl}/${source.pmtilesFile}`,
          promoteId: source.promoteId,
        });
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;
    layerEntries.forEach((layer) => {
      if (!map.getLayer(layer.id)) map.addLayer(layer, layerAfterRoadLabelsId);
    });

    Object.entries(inactiveSourceLayers).forEach(([layerName, paintProperties]) => {
      Object.entries(paintProperties).forEach(([property, value]) => {
        map.setPaintProperty(layers[layerName].id, property, value);
      });
    });
    const activeRwziId = Number(rwziId);
    map.setFilter(layers.rwzi.id, ['!=', ['id'], activeRwziId]);
    map.addSource(activeRwziSourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: data?.geometry,
        properties: {},
      },
    });
    map.addLayer({
      id: activeRwziLayerId,
      type: 'circle',
      source: activeRwziSourceId,
      paint: {
        'circle-color': getSourceColorExpression('potential', 'rwzi'),
        'circle-radius': 7,
        'circle-opacity': 1,
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 1,
      },
    }, layerAfterRoadLabelsId);
    const isActiveRwzi = ['case', ['==', ['id'], activeRwziId], true, false];
    map.setPaintProperty(layers.rwzi.id, 'circle-color', [
      'case',
      isActiveRwzi,
      getSourceColorExpression('potential', 'rwzi'),
      [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#217F9A',
        '#657F8C',
      ],
    ]);
    map.setPaintProperty(layers.rwzi.id, 'circle-radius', [
      'case',
      isActiveRwzi,
      7,
      [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        7,
        5,
      ],
    ]);
    map.setPaintProperty(layers.rwzi.id, 'circle-opacity', [
      'case',
      isActiveRwzi,
      1,
      0.65,
    ]);
    map.setPaintProperty(layers.rwzi.id, 'circle-stroke-color', [
      'case',
      isActiveRwzi,
      '#fff',
      [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#217F9A',
        '#657F8C',
      ],
    ]);
    map.setPaintProperty(layers.rwzi.id, 'circle-stroke-width', [
      'case',
      isActiveRwzi,
      2,
      [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        2,
        1,
      ],
    ]);
    map.setPaintProperty(layers.rwzi.id, 'circle-stroke-opacity', [
      'case',
      isActiveRwzi,
      1,
      0.65,
    ]);

    const navigationTargets = {
      [layers.flowingWaterHitArea.id]: feature => `/bronnen/stromend-water/${feature.id}`,
      [layers.stillWater.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.stillWaterOutline.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.rwzi.id]: feature => `/bronnen/rwzi/${feature.id}`,
    };
    const clickHandlers = Object.entries(navigationTargets).map(([layerId, getPath]) => {
      const handler = (event) => {
        const feature = event.features?.[0];
        if (feature?.id !== undefined && feature?.id !== null) {
          navigate(getPath(feature));
        }
      };
      map.on('click', layerId, handler);
      return { layerId, handler };
    });

    const interactiveLayerIds = Object.keys(navigationTargets);
    let hoveredFeature;
    const clearHoveredFeature = () => {
      if (!hoveredFeature) return;
      map.setFeatureState(hoveredFeature, { hover: false });
      hoveredFeature = undefined;
    };
    const handleMouseMove = (event) => {
      const feature = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      })[0];
      if (!feature) {
        clearHoveredFeature();
        map.getCanvas().style.cursor = '';
        return;
      }
      if (
        feature.source === sources.rwzi.id
        && String(feature.id) === String(rwziId)
      ) {
        clearHoveredFeature();
        map.getCanvas().style.cursor = '';
        return;
      }

      const featureStateTarget = {
        source: feature.source,
        sourceLayer: feature.sourceLayer,
        id: feature.id,
      };
      if (
        featureStateTarget.id !== undefined
        && featureStateTarget.id !== null
        && (
          !hoveredFeature
          || hoveredFeature.id !== featureStateTarget.id
          || hoveredFeature.source !== featureStateTarget.source
        )
      ) {
        clearHoveredFeature();
        hoveredFeature = featureStateTarget;
        map.setFeatureState(hoveredFeature, { hover: true });
      }
      map.getCanvas().style.cursor = 'pointer';
    };
    map.on('mousemove', handleMouseMove);

    return () => {
      clickHandlers.forEach(({ layerId, handler }) => map.off('click', layerId, handler));
      map.off('mousemove', handleMouseMove);
      clearHoveredFeature();
      map.getCanvas().style.cursor = '';
      if (map.getLayer(activeRwziLayerId)) {
        map.removeLayer(activeRwziLayerId);
      }
      if (map.getSource(activeRwziSourceId)) {
        map.removeSource(activeRwziSourceId);
      }
      if (map.getLayer(layers.rwzi.id)) {
        map.setFilter(layers.rwzi.id, null);
      }
      [...layerEntries].reverse().forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      [...sourceEntries].reverse().forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [data, map, navigate, rwziId]);

  return (
    <div className="sewage-water">
      <PanelHeader
        map={map}
        rootPath="/bronnen"
        currentName={name}
      />
      <PanelList details={details} />
    </div>
  );
}

export default SewageWater;
